/**
 * Platform Auth Abstraction
 * 
 * Handles authentication differently based on whether we're running
 * in a web browser or native Flutter app.
 */

import { bridge, BridgeChannels, BridgeActions } from '../bridge';

export interface NativeSignInResult {
  success: boolean;
  userId?: string;
  token?: string;
  error?: string;
}

export interface AccountDialogResult {
  action: 'signOut' | 'closed';
}

export interface AuthPlatform {
  /** Get current auth token */
  getToken(): Promise<string | null>;
  
  /** Sign out the user */
  signOut(): Promise<void>;
  
  /** Send credentials to native for storage */
  setCredentials(userId: string, token: string): Promise<void>;
  
  /** Listen for auth state changes from native */
  onAuthStateChange(callback: (state: { userId: string | null; token: string | null }) => void): () => void;
  
  /** Show native sign-in dialog (only works on native) */
  showNativeSignIn(): Promise<NativeSignInResult>;
  
  /** Show native account dialog (only works on native) */
  showAccountDialog(): Promise<AccountDialogResult>;
}

class WebAuth implements AuthPlatform {
  private getTokenFn: (() => Promise<string | null>) | null = null;
  private signOutFn: (() => Promise<void>) | null = null;

  /** Set the Clerk getToken function */
  setGetTokenFn(fn: () => Promise<string | null>) {
    this.getTokenFn = fn;
  }

  /** Set the Clerk signOut function */
  setSignOutFn(fn: () => Promise<void>) {
    this.signOutFn = fn;
  }

  async getToken(): Promise<string | null> {
    if (!this.getTokenFn) {
      console.warn('[WebAuth] getToken function not set');
      return null;
    }
    return this.getTokenFn();
  }

  async signOut(): Promise<void> {
    if (this.signOutFn) {
      await this.signOutFn();
    }
  }

  async setCredentials(_userId: string, _token: string): Promise<void> {
    // Web doesn't need to manually store credentials, Clerk handles it
  }

  onAuthStateChange(_callback: (state: { userId: string | null; token: string | null }) => void): () => void {
    // Clerk handles auth state via ClerkProvider
    return () => {};
  }

  async showNativeSignIn(): Promise<NativeSignInResult> {
    // Web doesn't use native sign-in, return failure
    return { success: false, error: 'Not running in native context' };
  }

  async showAccountDialog(): Promise<AccountDialogResult> {
    // Web doesn't use native account dialog
    return { action: 'closed' };
  }
}

class NativeAuth implements AuthPlatform {
  async getToken(): Promise<string | null> {
    try {
      return await bridge.request<string | null>(
        BridgeChannels.AUTH, 
        BridgeActions.GET_TOKEN
      );
    } catch (e) {
      console.error('[NativeAuth] getToken error:', e);
      return null;
    }
  }

  async signOut(): Promise<void> {
    try {
      await bridge.request(BridgeChannels.AUTH, BridgeActions.SIGN_OUT);
    } catch (e) {
      console.error('[NativeAuth] signOut error:', e);
    }
  }

  async setCredentials(userId: string, token: string): Promise<void> {
    try {
      await bridge.request(BridgeChannels.AUTH, BridgeActions.SET_CREDENTIALS, {
        userId,
        token,
      });
    } catch (e) {
      console.error('[NativeAuth] setCredentials error:', e);
    }
  }

  onAuthStateChange(callback: (state: { userId: string | null; token: string | null }) => void): () => void {
    return bridge.on(BridgeChannels.AUTH, BridgeActions.STATE_CHANGED, (state) => {
      callback(state);
    });
  }

  async showNativeSignIn(): Promise<NativeSignInResult> {
    try {
      const result = await bridge.request<NativeSignInResult>(
        BridgeChannels.AUTH,
        BridgeActions.SHOW_NATIVE_SIGN_IN,
        undefined,
        60000 * 10 // 10 minutes
      );
      return result;
    } catch (e) {
      console.error('[NativeAuth] showNativeSignIn error:', e);
      return { success: false, error: String(e) };
    }
  }

  async showAccountDialog(): Promise<AccountDialogResult> {
    try {
      const result = await bridge.request<AccountDialogResult>(
        BridgeChannels.AUTH,
        BridgeActions.SHOW_ACCOUNT_DIALOG,
        undefined,
        60000 * 5 // 5 minutes
      );
      return result;
    } catch (e) {
      console.error('[NativeAuth] showAccountDialog error:', e);
      return { action: 'closed' };
    }
  }
}

// Singleton instances
const webAuth = new WebAuth();
const nativeAuth = new NativeAuth();

export function getAuthPlatform(): AuthPlatform {
  return bridge.isNative ? nativeAuth : webAuth;
}

// Export webAuth for Clerk integration
export { webAuth };

